import { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import CustomerBook from "./CustomerBook";
import { LANGUAGE_NAMES, type GuestLanguage, type GuestStatus, type RentalGuestEntry } from "../worker/lib/rental-guests";

// Sezione «Ospiti» come su Booking: chi è in casa ora, chi arriva, chi ha già soggiornato.
// Solo prenotazioni confermate (anche pagate online); lo stesso ospite compare una volta con il numero di soggiorni.

const STATUS_LABEL: Record<GuestStatus, string> = { in_house: "In casa ora", arriving: "In arrivo", stayed: "Ha già soggiornato" };
type Filter = "all" | GuestStatus;

function day(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}
function fullDay(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
}
function bookedOn(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "numeric", month: "short", year: "numeric" });
}

export default function RentalGuests({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const [guests, setGuests] = useState<RentalGuestEntry[] | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState("");
  const [showBook, setShowBook] = useState(false);

  useEffect(() => {
    let active = true;
    api<{ guests: RentalGuestEntry[] }>(`/api/sites/${siteId}/rental-guests`)
      .then((result) => { if (active) setGuests(result.guests || []); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "Ospiti non disponibili"); });
    return () => { active = false; };
  }, [siteId]);

  const counts = useMemo(() => {
    const result: Record<GuestStatus, number> = { in_house: 0, arriving: 0, stayed: 0 };
    for (const guest of guests || []) result[guest.status] += 1;
    return result;
  }, [guests]);

  const list = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const digits = needle.replace(/\D/g, "");
    return (guests || []).filter((guest) => {
      if (filter !== "all" && guest.status !== filter) return false;
      if (!needle) return true;
      return `${guest.name} ${guest.email} ${guest.countryName}`.toLowerCase().includes(needle) || (digits.length >= 3 && guest.phone.replace(/\D/g, "").includes(digits));
    });
  }, [guests, query, filter]);

  if (!guests) return <div className="escape-empty">{error || "Caricamento ospiti…"}</div>;

  return <section className="rental-guests">
    <div className="rental-guests-head">
      <div className="rental-views" role="tablist" aria-label="Ospiti">
        <button role="tab" aria-selected={filter === "all"} className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>Tutti ({guests.length})</button>
        <button role="tab" aria-selected={filter === "in_house"} className={filter === "in_house" ? "active" : ""} onClick={() => setFilter("in_house")}>In casa ora ({counts.in_house})</button>
        <button role="tab" aria-selected={filter === "arriving"} className={filter === "arriving" ? "active" : ""} onClick={() => setFilter("arriving")}>In arrivo ({counts.arriving})</button>
        <button role="tab" aria-selected={filter === "stayed"} className={filter === "stayed" ? "active" : ""} onClick={() => setFilter("stayed")}>Già soggiornato ({counts.stayed})</button>
      </div>
      <input className="rental-guests-search" type="search" placeholder="Cerca nome, telefono, paese…" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Cerca ospite" />
    </div>
    <p className="rental-guests-note">Qui trovi solo le prenotazioni confermate (anche quelle pagate online). Le richieste da confermare restano in «Richieste».</p>

    {list.length === 0 ? <div className="escape-empty">{guests.length ? "Nessun ospite corrisponde alla ricerca." : "Ancora nessun soggiorno confermato: gli ospiti compariranno qui appena confermi una prenotazione."}</div>
      : <ul className="rental-guest-list">{list.map((guest) => <li key={guest.key} className={`rental-guest is-${guest.status}`}>
        <div className="rental-guest-who">
          <span className="rental-guest-flag" aria-hidden="true">{guest.flag || "🏳️"}</span>
          <div>
            <strong>{guest.name || "Ospite"}</strong>
            <small>{guest.countryName || "Paese non indicato"}{guest.language && LANGUAGE_NAMES[guest.language as GuestLanguage] ? ` · parla ${LANGUAGE_NAMES[guest.language as GuestLanguage].toLowerCase()}` : ""}</small>
          </div>
          <span className={`rental-guest-status is-${guest.status}`}>{STATUS_LABEL[guest.status]}</span>
        </div>
        <div className="rental-guest-stay">
          <span className="rental-house">{guest.stay.house}</span>
          <strong>{day(guest.stay.checkIn)} → {fullDay(guest.stay.checkOut)}</strong>
          <small>{guest.stay.nights} {guest.stay.nights === 1 ? "notte" : "notti"} · {guest.stay.guests} ospiti · prenotato il {bookedOn(guest.stay.bookedAt)}</small>
          {guest.stays > 1 && <button type="button" className="rental-guest-repeat" aria-expanded={open === guest.key} onClick={() => setOpen(open === guest.key ? "" : guest.key)}>
            ↺ {guest.stays} soggiorni {open === guest.key ? "· chiudi" : "· vedi tutti"}
          </button>}
        </div>
        <div className="rental-guest-contacts">
          {guest.whatsapp && <a className="rental-btn rental-btn-confirm" href={`https://wa.me/${guest.whatsapp}`} target="_blank" rel="noopener">WhatsApp</a>}
          {guest.phone && <a className="rental-btn rental-btn-restore" href={`tel:${guest.phone.replace(/[^\d+]/g, "")}`}>Chiama</a>}
          {guest.email && <a className="rental-btn rental-btn-restore" href={`mailto:${guest.email}`}>Email</a>}
        </div>
        {open === guest.key && <ol className="rental-guest-history">{guest.history.map((stay) => <li key={stay.requestId}>
          <span>{fullDay(stay.checkIn)} → {fullDay(stay.checkOut)}</span><span>{stay.house} · {stay.nights} notti · {stay.guests} ospiti</span>
        </li>)}</ol>}
      </li>)}</ul>}

    <div className="rental-guest-book">
      <button type="button" className="secondary" aria-expanded={showBook} onClick={() => setShowBook(!showBook)}>{showBook ? "Chiudi la rubrica completa" : "Rubrica completa e download CSV"}</button>
      {/* Caricata solo quando serve: legge e decifra di nuovo tutte le richieste. */}
      {showBook && <CustomerBook endpoint={`/api/sites/${siteId}/rental-guests`} canEdit={canEdit} mode="rental" />}
    </div>
  </section>;
}
